/**
 * PLT-7899: one row of the own-insight cards in chat — the proposal card, the carry-over card and the
 * undo card — so the three read alike. The headline names the impact, risk or opportunity the change
 * touches ("Disable risk: Carbon pricing raises fleet fuel costs") and the matter it sits in is the
 * muted line beneath, so a row never reads as a change to a whole matter.
 *
 * Everything sits in one column beside the checkbox. A second column at the right edge took the width
 * of whatever it held, and a long source citation there squeezed the headline to a letter per line in
 * the chat sidebar.
 */
import type { ReactNode } from 'react';
import clsx from 'clsx';
import { Checkbox } from '@upright/ui/common/generic/components/checkbox';
import { Link } from '@upright/ui/common/generic/components/link';
import { Text } from '@upright/ui/common/generic/components/typography';
import { VisuallyHidden } from '@upright/ui/common/generic/components/visually-hidden';
import styles from './own-insight-cards.module.css';

/**
 * "<action>: <IRO name>" when both are known; the action alone for a threshold change; else the
 * model's own summary, which proposals made before the server built rows still carry.
 */
export function ownInsightHeadline(parts: { action?: string; iroName?: string }, fallback: string): string {
  if (parts.action && parts.iroName) return `${parts.action}: ${parts.iroName}`;
  return parts.action ?? fallback;
}

export function OwnInsightRow({
  headline,
  meta,
  change,
  source,
  action,
  details,
  selection,
}: {
  headline: string;
  /** The muted line under the headline: "In Health and safety", "Applies to every matter". */
  meta?: ReactNode;
  /** What the change does to the IRO: levels, score and materiality, each a `ChangePart` or a badge. */
  change?: ReactNode;
  /** Where the change comes from, a muted line that wraps: a citation can be a sentence long. */
  source?: string;
  /** The row's last line, such as the details toggle. */
  action?: ReactNode;
  /** The expanded details, under the row's text. */
  details?: ReactNode;
  /** Absent on a read-only row, which then has no checkbox column. */
  selection?: { isSelected: boolean; isDisabled: boolean; onChange: (selected: boolean) => void };
}) {
  return (
    <div role="listitem" className={clsx(styles.row, !selection && styles.rowPlain)}>
      {selection && (
        <Checkbox
          alignment="top"
          isSelected={selection.isSelected}
          isDisabled={selection.isDisabled}
          onChange={selection.onChange}
        >
          <VisuallyHidden>Include {headline}</VisuallyHidden>
        </Checkbox>
      )}
      <div className={styles.rowMain}>
        <Text size="md" height="sm" weight="semibold" color="gray-900">
          {headline}
        </Text>
        {meta !== undefined && (
          <Text size="sm" height="xs" color="gray-600">
            {meta}
          </Text>
        )}
        {change !== undefined && <div className={styles.change}>{change}</div>}
        {source !== undefined && (
          <Text size="sm" height="xs" color="gray-600">
            Source: {source}
          </Text>
        )}
        {action}
      </div>
      {details !== undefined && <div className={styles.details}>{details}</div>}
    </div>
  );
}

/** One item of a row's change line ("Scale Low 2 → High 4"); the line spaces its items apart. */
export function ChangePart({ children }: { children: ReactNode }) {
  return (
    <Text size="sm" height="xs" color="gray-800">
      {children}
    </Text>
  );
}

/** The rows of one card, framed as one list. */
export function OwnInsightRows({ children }: { children: ReactNode }) {
  return (
    <div role="list" className={styles.rows}>
      {children}
    </div>
  );
}

export function DetailsToggle({ isOpen, onToggle }: { isOpen: boolean; onToggle: () => void }) {
  return (
    <Text size="sm" height="xs">
      <Link onPress={onToggle}>{isOpen ? 'Hide details' : 'Show details'}</Link>
    </Text>
  );
}

/** "In Climate change mitigation", or "Applies to every matter" for a threshold change. */
export function matterLine(matterName: string | undefined, ...extra: Array<string | undefined>): string {
  const head = matterName ? `In ${matterName}` : 'Applies to every matter';
  return [head, ...extra.filter((part): part is string => part !== undefined && part !== '')].join(' · ');
}
